import { Component, HostListener, inject, OnInit } from "@angular/core";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { Location } from "@angular/common";
import { ActivatedRoute, NavigationEnd, Router, Event as RouterEvent } from "@angular/router";
import { RouteDataService } from "./services/route-data.service";
import { Subscription, filter, map, switchMap } from "rxjs";
import { RouteData } from "@models/map.model";
import { AuthService } from "./services/auth/auth.service";
import { CartItemService } from "./services/cart-item/cart-item.service";
import { WishlistService } from "./services/wishlistService/wishlist.service";
import { TranslateService } from "@ngx-translate/core";
import { ThemeService } from "./services/theme-service";
import { IconService } from "./services/icon-service";

interface IconPaths {
    [key: string]: string;
}

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    standalone: false,
})
export class AppComponent implements OnInit {
    public isShowHeader = false;
    public showFooter = true;
    public isShowBackButton = true;
    public isAdminPage: boolean = false;
    private subscriptions: Subscription = new Subscription();
    public showProductDetailsMessage = false;
    private previousNavigationId: number | null = null;
    public isFromMail: boolean = false;
    public showTimeLine: boolean = false;
    public currentStep = 1;
    private iconPaths: IconPaths = {
        udm: "/assets/udm_icon.svg",
        funFigz: "/assets/funFigz-logo.svg",
        checkMark: "/assets/check-mark.svg",
        emptyCart: "/assets/empty-cart.svg",
        zoomIn: "/assets/zoom-in.svg",
        zoomOut: "/assets/zoom-out.svg",
        instagram: "/assets/instagram.svg",
        twitter: "/assets/twitter.svg",
        facebook: "/assets/facebook.svg",
        emptyNotification: "/assets/noNotification.svg",
        emptyWishlist: "/assets/emptyWishlist.svg",
        rewards: "/assets/rewards.svg",
        noReward: "/assets/noReward.svg",
        emptyStructure: "/assets/noStructure.svg",
    };

    readonly themeService = inject(ThemeService);
    readonly iconService = inject(IconService);

    constructor(
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private router: Router,
        private route: ActivatedRoute,
        public routeDataService: RouteDataService,
        private location: Location,
        private authService: AuthService,
        private cartService: CartItemService,
        private wishlistService: WishlistService,
        private translateService: TranslateService,
    ) {
        this.registerIcons();
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.showFooter = this.canShowFooter(event.url);
            }
        });

        let currentTab = true;
        document.addEventListener("visibilitychange", () => {
            currentTab = !document.hidden;
        });
        const channel = new BroadcastChannel(""); // to communicate tabs

        channel.onmessage = (event) => {
            const currentUrl = document.location.href;
            const isAdmin = currentUrl.includes("admin");
            const isLogout = event.data === "userLogout";

            if (!currentTab) {
                if (isAdmin && event.data !== "userLogout") {
                    this.router.navigate(["/admin/login"]);
                } else if (!isAdmin && isLogout) {
                    this.router.navigate(["/home"]);
                }
            }
        };
        this.translateService.setDefaultLang("en");
    }

    ngOnInit(): void {
        this.route.queryParamMap.subscribe((params) => {
            const from = params.get("from");
            if (from === "mail") {
                this.isFromMail = true;
            } else {
                this.isFromMail = false;
            }
        });
        this.authService.checkLoginStatus();
        const path = window.location.pathname;
        if (!(path.includes("admin") || path.includes("success"))) {
            this.authService.isLoggedIn$.subscribe((loginStatus) => {
                const isUserLogin = loginStatus;
                if (isUserLogin) {
                    this.setItemsLocalStorage();
                } else {
                    this.cartService.getCartItems().subscribe();
                }
            });
        }
        const routerEventSubscription = this.router.events
            .pipe(
                filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
                switchMap((event: NavigationEnd) => {
                    window.scrollTo(0, 0);
                    const bodyWrap = document.querySelector(".body-wrap") as HTMLElement;
                    if (bodyWrap) {
                        if (event.url.includes("admin")) {
                            bodyWrap.classList.add("admin");
                        } else {
                            bodyWrap.classList.remove("admin");
                        }
                    }
                    return this.routeDataService
                        .get({
                            noHeader: false,
                            noBackButton: false,
                        })
                        .pipe(map((data: RouteData) => ({ data, url: event.urlAfterRedirects })));
                }),
            )
            .subscribe(({ data, url }) => {
                this.isAdminPage = url.startsWith("/admin");
                this.updateHeaderAndBackButtonVisibility(data, url);
                this.showProductDetailsMessage =
                    (/products\/\d+/.test(url) && !url.includes("admin")) || url.includes("home");

                // Check if navigation ID is 1 and not repeating for the same session
                if (this.authService.isUserLoggedIn && !url.startsWith("/admin") && !url.includes("designer")) {
                    if (!history.state) {
                        return;
                    }
                    const currentNavigationId = history.state.navigationId;
                    // Update the previous navigation ID to prevent multiple hits
                    this.previousNavigationId = currentNavigationId;
                }
            });
        this.subscriptions.add(routerEventSubscription);
    }

    private updateHeaderAndBackButtonVisibility(
        data: {
            noBackButton: boolean;
            noHeader: boolean;
        },
        url: string,
    ): void {
        const isAdminLoginPage = url.startsWith("/admin") || url.startsWith("/designer");
        if (isAdminLoginPage) {
            this.isShowHeader = false;
        } else {
            this.isShowHeader = true;
        }

        this.isShowBackButton = !data.noBackButton && !this.isFromMail;

        this.showTimeLine = url.includes("cart");
    }

    private registerIcons() {
        Object.keys(this.iconPaths).forEach((key) => {
            const iconPath = this.iconPaths[key];
            this.matIconRegistry.addSvgIcon(key, this.domSanitizer.bypassSecurityTrustResourceUrl(iconPath));
        });
    }

    // Designer takes the entire screen as canvas, footer wont be necessary
    private canShowFooter(url: string): boolean {
        // If it contains 'designer' but not 'admin', hide footer
        if (url.includes("designer") && !url.includes("admin")) {
            return false;
        }

        // Otherwise, always show footer (including user module)
        return true;
    }

    @HostListener("window:scroll", ["$event"])
    onScroll(event: Event): void {
        const header = document.getElementById("header");
        if (window.scrollY > 15) {
            if (!header?.classList.contains("sticky")) {
                header?.classList.add("sticky");
            }
        } else {
            header?.classList.remove("sticky");
        }
    }

    private setItemsLocalStorage() {
        this.wishlistService.loadMyWishlistItems();
        this.cartService.processCartItems(false);
    }

    public goBack(): void {
        this.location.back();
    }
}
